import React from 'react';
import { FileText, Scan, Brain, Layers, FileSpreadsheet } from 'lucide-react';
import { useParserState } from './useParserState';
import UploadPhase from './components/UploadPhase';
import { ClassifyingPhase } from './components/ClassifyingPhase';
import { ClassificationResultPhase } from './components/ClassificationResultPhase';
import { ProcessingPhase } from './components/ProcessingPhase';
import { CompletedPhase } from './components/CompletedPhase';
import { ErrorPhase } from './components/ErrorPhase';
import { AiDescribePhase } from './components/AiDescribePhase';
import { AiProcessingPhase } from './components/AiProcessingPhase';
import { AiPreviewPhase } from './components/AiPreviewPhase';
import { PhaseBackground } from './components/PhaseBackground';
import type { ProcessingStep } from './types';

const processingSteps: ProcessingStep[] = [
  { label: 'Text Extraction', desc: 'Extracting content from document', icon: <FileText className="w-4 h-4" /> },
  { label: 'Structure Analysis', desc: 'Identifying requirements', icon: <Scan className="w-4 h-4" /> },
  { label: 'AI Normalization', desc: 'Standardizing format', icon: <Brain className="w-4 h-4" /> },
  { label: 'Template Creation', desc: 'Building structure', icon: <Layers className="w-4 h-4" /> },
  { label: 'Field Generation', desc: 'Creating data fields', icon: <FileSpreadsheet className="w-4 h-4" /> },
];

const FrameworkParser: React.FC = () => {
  const parser = useParserState();

  const renderPhase = () => {
    switch (parser.currentPhase) {
      case 'upload':
        return (
          <UploadPhase
            currentPhase={parser.currentPhase}
            currentStepIndex={parser.currentStepIndex}
            steps={parser.steps}
            selectedFile={parser.selectedFile}
            error={parser.error}
            parsingMethod={parser.parsingMethod}
            aiDescription={parser.aiDescription}
            stats={parser.stats}
            statsLoading={parser.statsLoading}
            setParsingMethod={parser.setParsingMethod}
            setSelectedFile={parser.setSelectedFile}
            setAiDescription={parser.setAiDescription}
            setCurrentPhase={parser.setCurrentPhase}
            setError={parser.setError}
            handleFileSelect={parser.handleFileSelect}
            classifyDocument={parser.classifyDocument}
          />
        );

      case 'classifying':
        return (
          <PhaseBackground variant="blue">
            <ClassifyingPhase
              steps={parser.steps}
              currentStep={parser.currentStepIndex}
              selectedFile={parser.selectedFile}
            />
          </PhaseBackground>
        );

      case 'classification_result':
        return parser.classificationResult ? (
          <PhaseBackground variant="blue">
            <ClassificationResultPhase
              classificationResult={parser.classificationResult}
              steps={parser.steps}
              currentStep={parser.currentStepIndex}
              onReset={parser.resetParser}
              onProceed={parser.proceedWithProcessing}
            />
          </PhaseBackground>
        ) : null;

      case 'processing':
        return (
          <PhaseBackground variant="blue">
            <ProcessingPhase
              steps={parser.steps}
              currentStep={parser.currentStepIndex}
              processingSteps={processingSteps}
              selectedFile={parser.selectedFile}
              processingProgress={parser.processingProgress}
              parserLog={parser.parserLog}
              stats={parser.stats}
              statsLoading={parser.statsLoading}
            />
          </PhaseBackground>
        );

      case 'completed':
        return parser.ingestionResult ? (
          <PhaseBackground variant="green">
            <CompletedPhase
              ingestionResult={parser.ingestionResult}
              steps={parser.steps}
              currentStep={parser.currentStepIndex}
              onReset={parser.resetParser}
              onViewTemplate={parser.viewTemplate}
            />
          </PhaseBackground>
        ) : null;

      case 'error':
        return (
          <PhaseBackground variant="red">
            <ErrorPhase
              error={parser.error}
              steps={parser.steps}
              currentStep={parser.currentStepIndex}
              onReset={parser.resetParser}
              onRetry={() => parser.setCurrentPhase('upload')}
            />
          </PhaseBackground>
        );

      case 'ai_describe':
        return (
          <PhaseBackground variant="purple">
            <AiDescribePhase />
          </PhaseBackground>
        );

      case 'ai_processing':
        return (
          <PhaseBackground variant="purple">
            <AiProcessingPhase />
          </PhaseBackground>
        );

      case 'ai_preview':
        return (
          <PhaseBackground variant="purple">
            <AiPreviewPhase
              aiDraft={parser.aiDraft}
              onBack={() => parser.setCurrentPhase('upload')}
            />
          </PhaseBackground>
        );

      default:
        return (
          <UploadPhase
            currentPhase={parser.currentPhase}
            currentStepIndex={parser.currentStepIndex}
            steps={parser.steps}
            selectedFile={parser.selectedFile}
            error={parser.error}
            parsingMethod={parser.parsingMethod}
            aiDescription={parser.aiDescription}
            stats={parser.stats}
            statsLoading={parser.statsLoading}
            setParsingMethod={parser.setParsingMethod}
            setSelectedFile={parser.setSelectedFile}
            setAiDescription={parser.setAiDescription}
            setCurrentPhase={parser.setCurrentPhase}
            setError={parser.setError}
            handleFileSelect={parser.handleFileSelect}
            classifyDocument={parser.classifyDocument}
          />
        );
    }
  };

  return renderPhase();
};

export default FrameworkParser;
